import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { checklistInstancesApi, ChecklistItemInstance } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { isChecklistInstanceOverdue } from '../utils/overdue';

export function ChecklistCalendarPage() {
  const { hapticFeedback } = useTelegram();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

  // Используем соответствующий период в зависимости от режима просмотра
  const periodStart = viewMode === 'month' ? monthStart : weekStart;
  const periodEnd = viewMode === 'month' ? monthEnd : weekEnd;

  const { data: instances, isLoading } = useQuery({
    queryKey: ['checklist-instances-calendar', periodStart, periodEnd, viewMode],
    queryFn: () =>
      checklistInstancesApi.list({
        fromDate: periodStart.toISOString(),
        toDate: periodEnd.toISOString(),
      }),
  });

  const getInstancesForDate = (date: Date): ChecklistItemInstance[] => {
    if (!instances) return [];
    return instances.filter((inst) => {
      const instDate = new Date(inst.plannedDate);
      return isSameDay(instDate, date);
    });
  };

  // Генерируем дни для отображения в зависимости от режима
  const calendarDays = viewMode === 'month' 
    ? eachDayOfInterval({ start: monthStart, end: monthEnd })
    : eachDayOfInterval({ start: weekStart, end: weekEnd });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <CalendarIcon className="w-6 h-6" />
          Календарь
        </h1>
        <div className="flex gap-2">
          {/* Переключатель Календарь / Список */}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              navigate('/checklists/plan');
              hapticFeedback('light');
            }}
          >
            Список
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'month' ? 'primary' : 'secondary'}
            onClick={() => {
              setViewMode('month');
              hapticFeedback('light');
            }}
          >
            Месяц
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'week' ? 'primary' : 'secondary'}
            onClick={() => {
              setViewMode('week');
              // При переключении на неделю устанавливаем текущую неделю
              if (viewMode === 'month') {
                setCurrentWeek(new Date());
              }
              hapticFeedback('light');
            }}
          >
            Неделя
          </Button>
        </div>
      </div>

      {/* Навигация по месяцам/неделям */}
      <div className="flex items-center justify-between">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (viewMode === 'month') {
              setCurrentMonth(subMonths(currentMonth, 1));
            } else {
              setCurrentWeek(subWeeks(currentWeek, 1));
            }
            hapticFeedback('light');
          }}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-lg font-semibold text-tg-text">
          {viewMode === 'month' 
            ? format(currentMonth, 'MMMM yyyy', { locale: ru })
            : `${format(weekStart, 'd MMM', { locale: ru })} - ${format(weekEnd, 'd MMM yyyy', { locale: ru })}`
          }
        </h2>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (viewMode === 'month') {
              setCurrentMonth(addMonths(currentMonth, 1));
            } else {
              setCurrentWeek(addWeeks(currentWeek, 1));
            }
            hapticFeedback('light');
          }}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Календарь */}
      <Card className="p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-tg-hint py-2">
              {day}
            </div>
          ))}
        </div>
        <div className={`grid gap-1 ${viewMode === 'week' ? 'grid-cols-7' : 'grid-cols-7'}`}>
          {/* Для месячного вида добавляем пустые ячейки в начале, если месяц начинается не с понедельника */}
          {viewMode === 'month' && (() => {
            const firstDayOfWeek = (monthStart.getDay() + 6) % 7; // Понедельник = 0
            const emptyCells = Array(firstDayOfWeek).fill(null);
            return emptyCells.map((_, idx) => (
              <div key={`empty-start-${idx}`} className="aspect-square" />
            ));
          })()}
          {calendarDays.map((day) => {
            const dayInstances = getInstancesForDate(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = viewMode === 'week' || isSameMonth(day, currentMonth);

            return (
              <button
                key={day.toISOString()}
                onClick={() => {
                  setSelectedDate(day);
                  hapticFeedback('light');
                }}
                className={`
                  ${viewMode === 'week' ? 'min-h-[80px] p-2' : 'aspect-square p-1'} 
                  rounded-lg text-sm transition-colors
                  ${isCurrentMonth ? 'text-tg-text' : 'text-gray-300'}
                  ${isToday ? 'bg-blue-100 font-bold' : ''}
                  ${isSelected ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}
                `}
              >
                <div className={`${viewMode === 'week' ? 'text-sm font-semibold mb-2' : 'text-xs mb-1'}`}>
                  {viewMode === 'week' ? format(day, 'd MMM', { locale: ru }) : format(day, 'd')}
                </div>
                {dayInstances.length > 0 && (
                  <div className={`flex ${viewMode === 'week' ? 'flex-col gap-1' : 'flex-wrap gap-0.5'}`}>
                    {dayInstances.slice(0, viewMode === 'week' ? 5 : 3).map((inst) => {
                      const isOverdue = isChecklistInstanceOverdue(inst);
                      if (viewMode === 'week') {
                        return (
                          <div
                            key={inst.id}
                            className={`
                              text-xs p-1 rounded truncate
                              ${inst.status === 'ACCEPTED' || inst.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : ''}
                              ${inst.status === 'COMPLETED_PENDING' ? 'bg-yellow-100 text-yellow-800' : ''}
                              ${isOverdue ? 'bg-red-100 text-red-800' : ''}
                              ${inst.status === 'ASSIGNED' || inst.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' : ''}
                            `}
                            title={inst.rule?.text}
                          >
                            {inst.rule?.text?.substring(0, 20) || 'Задача'}...
                          </div>
                        );
                      }
                      return (
                        <div
                          key={inst.id}
                          className={`
                            w-1.5 h-1.5 rounded-full
                            ${inst.status === 'ACCEPTED' || inst.status === 'COMPLETED' ? 'bg-green-500' : ''}
                            ${inst.status === 'COMPLETED_PENDING' ? 'bg-yellow-500' : ''}
                            ${isOverdue ? 'bg-red-500' : ''}
                            ${inst.status === 'ASSIGNED' || inst.status === 'IN_PROGRESS' ? 'bg-blue-500' : ''}
                          `}
                        />
                      );
                    })}
                    {dayInstances.length > (viewMode === 'week' ? 5 : 3) && (
                      <div className={`${viewMode === 'week' ? 'text-xs text-tg-hint mt-1' : 'text-[8px] text-tg-hint'}`}>
                        +{dayInstances.length - (viewMode === 'week' ? 5 : 3)}
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Список задач выбранного дня */}
      {selectedDate && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-tg-text mb-3">
            {format(selectedDate, 'd MMMM yyyy', { locale: ru })}
          </h3>
          <div className="space-y-2">
            {getInstancesForDate(selectedDate).length === 0 ? (
              <p className="text-tg-hint text-center py-4">Нет задач на этот день</p>
            ) : (
              getInstancesForDate(selectedDate).map((instance) => {
                const isOverdue = isChecklistInstanceOverdue(instance);
                return (
                  <div
                    key={instance.id}
                    className="p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-tg-text">{instance.rule?.text}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant={
                              instance.status === 'ACCEPTED' || instance.status === 'COMPLETED'
                                ? 'success'
                                : instance.status === 'COMPLETED_PENDING'
                                ? 'warning'
                                : 'default'
                            }
                          >
                            {instance.status === 'ASSIGNED' && 'Назначена'}
                            {instance.status === 'IN_PROGRESS' && 'В работе'}
                            {instance.status === 'COMPLETED' && 'Выполнена'}
                            {instance.status === 'COMPLETED_PENDING' && 'Ожидает приёмки'}
                            {instance.status === 'ACCEPTED' && 'Принята'}
                            {instance.status === 'REJECTED' && 'Отклонена'}
                          </Badge>
                          {isOverdue && <Badge variant="danger">Просрочено</Badge>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      )}
    </div>
  );
}











